import { useQuery } from '@apollo/client';
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Checkbox,
  Fade,
  FormControlLabel,
  FormGroup,
  Radio,
  RadioGroup,
  Typography,
} from '@material-ui/core';
import {
  createMuiTheme,
  createStyles,
  makeStyles,
  Theme,
  ThemeProvider,
} from '@material-ui/core/styles';
import ColorThief from 'colorthief';
import gql from 'graphql-tag';
import React, { forwardRef, useCallback, useRef, useState } from 'react';
import FlipMove from 'react-flip-move';
import NumberEasing from 'react-number-easing';

//@ts-ignore
const colorThief = new ColorThief();

const GET_STATS = gql`
  query GetStats(
    $type: String!
    $time: String!
    $ergTypes: [String]
    $limit: Int
  ) {
    stats: recentStats(
      type: $type
      time: $time
      ergTypes: $ergTypes
      limit: $limit
    ) {
      name
      image
      total
    }
  }
`;

interface Stat {
  name: string;
  total: number;
  image: string;
}

const POLL_INTERVAL = 10000;
const ITEMS_PER_PAGE = 10;

const muiTheme = createMuiTheme({
  palette: {
    type: 'dark',
    primary: {
      main: '#009e9a',
      light: '#00C9BE',
      dark: '#007a76',
    },
    secondary: {
      main: '#d9915e',
      light: '#e6a97a',
      dark: '#b3744a',
    },
    background: {
      default: '#1a1a1a',
      paper: '#2d2d2d',
    },
    text: {
      primary: '#ffffff',
      secondary: '#b0b0b0',
    },
  },
  typography: {
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.25rem',
      fontWeight: 500,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.125rem',
      fontWeight: 500,
      lineHeight: 1.4,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.4,
    },
  },
  shape: {
    borderRadius: 12,
  },
});

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      margin: theme.spacing(1),
      minHeight: '100vh',
      padding: theme.spacing(1.5),
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(1),
      },
    },
    content: {
      maxWidth: 1400,
      margin: '0 auto',
    },
    headerCard: {
      backgroundColor: theme.palette.background.paper,
      border: '1px solid rgba(255, 255, 255, 0.12)',
      marginBottom: theme.spacing(2),
      boxShadow: 'none',
    },
    headerContent: {
      padding: theme.spacing(2),
      '&:last-child': {
        paddingBottom: theme.spacing(2),
      },
    },
    headerTop: {
      display: 'flex',
      alignItems: 'stretch',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: `${theme.spacing(1)}px`,
      [theme.breakpoints.down('md')]: {
        flexDirection: 'column',
        alignItems: 'stretch',
      },
    },
    title: {
      color: theme.palette.text.primary,
      fontWeight: 600,
      marginBottom: theme.spacing(1.5),
    },
    filterContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: `${theme.spacing(1.5)}px`,
      [theme.breakpoints.down('xs')]: {
        width: '100%',
      },
    },
    filterSection: {
      backgroundColor: '#252525',
      border: '1px solid rgba(255, 255, 255, 0.12)',
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(1.5),
    },
    filterTitle: {
      color: theme.palette.text.primary,
      marginBottom: theme.spacing(1),
      fontWeight: 500,
    },
    formLabel: {
      color: theme.palette.text.primary,
      fontWeight: 500,
      marginBottom: theme.spacing(1),
    },
    buttonGroup: {
      '& .MuiButton-root': {
        color: theme.palette.text.primary,
        borderColor: 'rgba(255, 255, 255, 0.2)',
        '&.MuiButton-contained': {
          backgroundColor: theme.palette.primary.main,
          color: '#ffffff',
          '&:hover': {
            backgroundColor: theme.palette.primary.dark,
          },
        },
        '&.MuiButton-outlined': {
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
          },
        },
      },
    },
    checkbox: {
      color: theme.palette.primary.main,
      '&.Mui-checked': {
        color: theme.palette.primary.main,
      },
    },
    radio: {
      color: theme.palette.primary.main,
      '&.Mui-checked': {
        color: theme.palette.primary.main,
      },
    },
    formControlLabel: {
      color: theme.palette.text.primary,
      marginRight: theme.spacing(1),
      marginLeft: 0,
      '& .MuiFormControlLabel-label': {
        color: theme.palette.text.primary,
      },
    },
    rowsContainer: {
      flex: 1,
      display: 'grid',
      gridTemplateColumns: '1fr',
      gap: `${theme.spacing(1)}px`,
    },
    row: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(0.75),
      backgroundColor: theme.palette.background.paper,
      border: '1px solid rgba(255, 255, 255, 0.12)',
      borderRadius: theme.shape.borderRadius,
      '&:hover': {
        backgroundColor: '#333333',
        borderColor: 'rgba(255, 255, 255, 0.2)',
      },
    },
    bar: {
      display: 'flex',
      flexShrink: 0,
      padding: theme.spacing(0.75),
      borderRadius: theme.shape.borderRadius,
      minWidth: theme.spacing(6),
      justifyContent: 'flex-end',
      transition: 'width 0.4s',
    },
    image: {
      width: theme.spacing(4),
      height: theme.spacing(4),
      objectFit: 'cover',
      borderRadius: '50%',
    },
    rowMetricContainer: {
      color: theme.palette.text.primary,
      minWidth: 0,
      marginLeft: theme.spacing(1.5),
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
    },
    rowName: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      fontWeight: 600,
      color: theme.palette.text.primary,
      marginBottom: theme.spacing(0.25),
    },
    rowValue: {
      color: theme.palette.text.secondary,
      fontWeight: 500,
    },
    loadMore: {
      marginTop: theme.spacing(2),
      color: theme.palette.text.primary,
      borderColor: 'rgba(255, 255, 255, 0.2)',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        borderColor: 'rgba(255, 255, 255, 0.3)',
      },
    },
    noData: {
      textAlign: 'center',
      color: theme.palette.text.secondary,
      padding: theme.spacing(2),
      fontSize: '1.1rem',
    },
    statsSection: {
      marginTop: theme.spacing(1),
    },
    sectionTitle: {
      color: theme.palette.text.primary,
      marginBottom: theme.spacing(1.5),
      fontWeight: 600,
    },
  })
);

const Row = forwardRef<HTMLDivElement, { stat: Stat; percentage: number }>(
  ({ stat, percentage }, ref) => {
    const styles = useStyles();
    const [color, setColor] = useState('rgba(255,255,255, 0.2)');
    const imageRef = useRef<HTMLImageElement>(null);

    const onLoad = useCallback(() => {
      if (imageRef.current) {
        try {
          const newColor = colorThief.getPalette(imageRef.current)[0];
          setColor(`rgba(${newColor[0]}, ${newColor[1]}, ${newColor[2]}, 0.9)`);
        } catch (error) {
          // Fallback to default color if color extraction fails
          setColor('rgba(255,255,255, 0.2)');
        }
      }
    }, []);

    return (
      <div className={styles.row} ref={ref}>
        <div
          className={styles.bar}
          style={{ backgroundColor: color, width: `${75 * percentage}%` }}
        >
          <img
            ref={imageRef}
            className={styles.image}
            src={stat.image}
            onLoad={onLoad}
            crossOrigin="anonymous"
            alt={`${stat.name} logo`}
          />
        </div>
        <div className={styles.rowMetricContainer}>
          <div className={styles.rowName}>{stat.name}</div>
          <div className={styles.rowValue}>
            <NumberEasing
              value={stat.total}
              speed={400}
              decimals={0}
              ease="quintInOut"
              customFunctionRender={(value) =>
                value.toFixed().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              }
            />
            {' m'}
          </div>
        </div>
      </div>
    );
  }
);

const defaultErgs = ['row', 'ski', 'bike', 'multi'];

const RecentStats = () => {
  const styles = useStyles();
  const [type, setType] = useState('group');
  const [time, setTime] = useState('1-day');
  const [ergTypes, setErgTypes] = useState(defaultErgs);
  const [stats, setStats] = useState<Stat[]>([]);
  const [page, setPage] = useState(1);

  const variables = {
    type,
    time,
    ergTypes: ergTypes.length === 0 ? defaultErgs : ergTypes,
    limit: page * ITEMS_PER_PAGE + 1,
  };

  const { loading } = useQuery(GET_STATS, {
    fetchPolicy: 'network-only',
    variables,
    pollInterval: POLL_INTERVAL,
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      setStats(data && data.stats ? data.stats : []);
    },
  });

  const onErgChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const erg = event.target.name;
    ergTypes.includes(erg)
      ? setErgTypes(ergTypes.filter((t) => t !== erg))
      : setErgTypes([...ergTypes, erg]);
  };

  const firstMetric = stats[0] ? stats[0].total : 1;

  return (
    <>
      <Box className={styles.container}>
        <Fade in timeout={500}>
          <Box className={styles.content}>
            {/* Header Section */}
            <Card className={styles.headerCard} elevation={0}>
              <CardContent className={styles.headerContent}>
                <Typography variant="h4" className={styles.title}>
                  Recent Stats
                </Typography>

                <Box className={styles.headerTop}>
                  {/* Type Selector */}
                  <Box className={styles.filterSection}>
                    <Typography variant="h6" className={styles.filterTitle}>
                      View By
                    </Typography>
                    <ButtonGroup className={styles.buttonGroup} color="primary">
                      <Button
                        variant={type === 'group' ? 'contained' : 'outlined'}
                        onClick={() => setType('group')}
                      >
                        Groups
                      </Button>
                      <Button
                        variant={type === 'country' ? 'contained' : 'outlined'}
                        onClick={() => setType('country')}
                      >
                        Countries
                      </Button>
                    </ButtonGroup>
                  </Box>

                  {/* Time Selector */}
                  <Box className={styles.filterSection}>
                    <Typography variant="h6" className={styles.filterTitle}>
                      Time Period
                    </Typography>
                    <RadioGroup
                      value={time}
                      onChange={(_, value) => setTime(value)}
                      row
                    >
                      <FormControlLabel
                        value="1-day"
                        control={<Radio className={styles.radio} />}
                        label="1 day"
                        className={styles.formControlLabel}
                      />
                      <FormControlLabel
                        value="7-day"
                        control={<Radio className={styles.radio} />}
                        label="7 days"
                        className={styles.formControlLabel}
                      />
                      <FormControlLabel
                        value="30-day"
                        control={<Radio className={styles.radio} />}
                        label="30 days"
                        className={styles.formControlLabel}
                      />
                    </RadioGroup>
                  </Box>

                  {/* Erg Type Filters */}
                  <Box className={styles.filterSection}>
                    <Typography variant="h6" className={styles.filterTitle}>
                      Erg Types
                    </Typography>
                    <FormGroup row>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={ergTypes.includes('row')}
                            onChange={onErgChange}
                            name="row"
                            className={styles.checkbox}
                          />
                        }
                        label="Row"
                        className={styles.formControlLabel}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={ergTypes.includes('ski')}
                            onChange={onErgChange}
                            name="ski"
                            className={styles.checkbox}
                          />
                        }
                        label="Ski"
                        className={styles.formControlLabel}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={ergTypes.includes('bike')}
                            onChange={onErgChange}
                            name="bike"
                            className={styles.checkbox}
                          />
                        }
                        label="Bike"
                        className={styles.formControlLabel}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={ergTypes.includes('multi')}
                            onChange={onErgChange}
                            name="multi"
                            className={styles.checkbox}
                          />
                        }
                        label="Multi"
                        className={styles.formControlLabel}
                      />
                    </FormGroup>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Stats Section */}
            <Box className={styles.statsSection}>
              <div className={styles.rowsContainer}>
                {!loading && stats.length === 0 && (
                  <Typography className={styles.noData}>
                    No data for this criteria
                  </Typography>
                )}
                <FlipMove typeName={null} duration={400}>
                  {stats
                    .slice(0, Math.min(stats.length, page * ITEMS_PER_PAGE))
                    .map((stat) => (
                      <Row
                        key={stat.name}
                        stat={stat}
                        percentage={stat.total / firstMetric}
                      />
                    ))}
                </FlipMove>
                {stats.length === page * ITEMS_PER_PAGE + 1 && (
                  <Button
                    className={styles.loadMore}
                    variant="outlined"
                    onClick={() => setPage((value) => value + 1)}
                    fullWidth
                  >
                    Load More
                  </Button>
                )}
              </div>
            </Box>
          </Box>
        </Fade>
      </Box>
    </>
  );
};

export default function RecentStatsWrapper() {
  return (
    <ThemeProvider theme={muiTheme}>
      <RecentStats />
    </ThemeProvider>
  );
}
